import React, { useState, useEffect } from "react";
import { observer } from "mobx-react-lite";
import blockStore from "../../Store/BlockStore";
import { getErrorMessage } from "../../utils/errorHandler";
import {
  Box,
  Modal,
  Paper,
  Typography,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  IconButton,
  CircularProgress,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowDownward from "@mui/icons-material/ArrowDownward";
import { toast } from "react-toastify";

const MainBodyBlockModal = observer(
  ({
    open,
    handleClose,
    movie,
    repeatLastFilmListRequest, // ← Новый пропс
  }) => {
    const [selectedBlockId, setSelectedBlockId] = useState("");
    const [selectedBlockName, setSelectedBlockName] = useState("");

    const [currentBlocks, setCurrentBlocks] = useState([]);

    // Загрузка при открытии
    useEffect(() => {
      if (open && movie) {
        blockStore.getBlockList();
        setSelectedBlockId("");
        setSelectedBlockName("");
      }
    }, [open, movie]);

    // Загрузка фильмов в выбранном блоке
    useEffect(() => {
      if (selectedBlockId) {
        blockStore.getMoviesInBlock(selectedBlockId);
      }
    }, [selectedBlockId]);

    // Определение блоков, где уже есть фильм
    useEffect(() => {
      if (movie && blockStore.responseBlockList?.data) {
        const blocksWithMovie = blockStore.responseBlockList.data.filter(
          (block) => block.movies?.some((m) => m.id === movie.id)
        );
        setCurrentBlocks(blocksWithMovie);
      }
    }, [movie, blockStore.responseBlockList]);

    const handleBlockSelect = (e) => {
      const blockId = e.target.value;
      setSelectedBlockId(blockId);

      const block = blockStore.responseBlockList?.data?.find(
        (b) => b.block_id === Number(blockId)
      );
      setSelectedBlockName(block?.name || "");
    };

    const handleAddToBlock = async () => {
      if (!selectedBlockId || !movie) return;
      try {
        await blockStore.addMovieToBlock(movie.id, Number(selectedBlockId));
        await blockStore.getMoviesInBlock(selectedBlockId);

        // ← Обновляем основной список фильмов
        if (repeatLastFilmListRequest) {
          repeatLastFilmListRequest();
        }

        toast.success("Фильм успешно добавлен в блок");
      } catch (error) {
        toast.error(
          `❌ ${getErrorMessage(error, "Не удалось добавить фильм в блок")}`
        );
      }
    };

    const handleRemoveFromBlock = async (recordId) => {
      if (!recordId) return;
      if (!window.confirm("Удалить фильм из этого блока?")) return;

      try {
        await blockStore.removeMovieFromBlock(
          recordId,
          Number(selectedBlockId)
        );
        if (selectedBlockId) {
          await blockStore.getMoviesInBlock(selectedBlockId);
        }
        toast.success("Фильм удалён из блока");
      } catch (error) {
        toast.error("Не удалось удалить фильм");
      }
    };

    const handleMoveDown = async (recordId, blockId) => {
      if (!recordId || !blockId) return;
      try {
        await blockStore.moveMovieDown(recordId, Number(blockId));
        await blockStore.getMoviesInBlock(selectedBlockId);
        toast.success("Фильм опущен вниз");
      } catch (error) {
        toast.error("Не удалось опустить фильм");
      }
    };

    if (!movie) return null;

    const moviesInBlock = blockStore.responseMoviesInBlock?.data || [];

    return (
      <Modal open={open} onClose={handleClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 560,
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
            maxHeight: "90vh",
            overflowY: "auto",
          }}
        >
          <Paper sx={{ p: 2, mb: 3, backgroundColor: "#E0F7FA" }}>
            <Typography variant="h6" align="center">
              {movie.title}
            </Typography>
          </Paper>

          {currentBlocks.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Фильм уже находится в блоках:
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableBody>
                    {currentBlocks.map((block) => (
                      <TableRow key={block.block_id}>
                        <TableCell>{block.name}</TableCell>
                        <TableCell align="right">
                          <IconButton
                            color="error"
                            size="small"
                            onClick={() => handleRemoveFromBlock(block.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          <Typography variant="subtitle1" gutterBottom>
            Добавить в блок:
          </Typography>

          <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
            <Select
              fullWidth
              value={selectedBlockId}
              onChange={handleBlockSelect}
              displayEmpty
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 300,
                  },
                },
              }}
            >
              <MenuItem value="" disabled>
                Выберите блок...
              </MenuItem>
              {blockStore.responseBlockList?.data?.map((block) => (
                <MenuItem key={block.block_id} value={block.block_id}>
                  {block.name}
                </MenuItem>
              ))}
            </Select>

            <Button
              variant="contained"
              onClick={handleAddToBlock}
              disabled={!selectedBlockId}
            >
              Добавить
            </Button>
          </Box>

          {selectedBlockId && (
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Фильмы в блоке «{selectedBlockName}»:
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableBody>
                    {blockStore.loading ? (
                      <TableRow>
                        <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                          <CircularProgress size={28} />
                        </TableCell>
                      </TableRow>
                    ) : moviesInBlock.length > 0 ? (
                      moviesInBlock.map((film) => (
                        <TableRow key={film.id}>
                          <TableCell>
                            {film.title ||
                              `Фильм ${film.movie?.title || film.movie_id}`}
                          </TableCell>

                          <TableCell align="right">
                            <IconButton
                              color="primary"
                              size="small"
                              onClick={() =>
                                handleMoveDown(film.id, film.block_id)
                              }
                              title="Опустить вниз"
                            >
                              <ArrowDownward />
                            </IconButton>
                          </TableCell>

                          <TableCell align="right">
                            <IconButton
                              color="error"
                              size="small"
                              onClick={() => handleRemoveFromBlock(film.id)}
                              title="Удалить фильм из блока"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={3} align="center">
                          В блоке пока нет фильмов
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          <Box sx={{ mt: 4, textAlign: "center" }}>
            <Button variant="outlined" onClick={handleClose}>
              Закрыть
            </Button>
          </Box>
        </Box>
      </Modal>
    );
  }
);

export default MainBodyBlockModal;
